import {ThreeTsl as TSL} from "#material/three/exports"
import type {TexCoordSockets} from "#material/nodes/texture/tex-coord/definition"
import {texCoordDefinition} from "#material/nodes/texture/tex-coord/definition"
import {MaterialNode} from "#material/nodes/base/material/material-node"
import {registerMaterialGraphNode} from "#material/register"
import {threeWindowCoordinates} from "#material/nodes/texture/tex-coord/primitives"
import {MaterialConnection} from "#material/nodes/base/connection/material-connection"
import type {TextureCoordinateNode} from "#material/cycles/types"
import type {ThreeReturns} from "#material/sockets/three"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import type {CyclesReturns} from "#material/sockets/cycles"

@registerMaterialGraphNode
export class TexCoord extends MaterialNode<TexCoordSockets> {
    public override get definition() {
        return texCoordDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<TexCoordSockets>> => {
        const camera = TSL.cameraPosition
        const generated = TSL.uv(0)
        const normal = TSL.normalLocal
        const object = TSL.positionLocal
        const reflection = TSL.reflectVector
        const uv = TSL.uv(0)

        const window = threeWindowCoordinates()
        const {onThreeRequestShaderAdditions} = evaluator.context
        if (onThreeRequestShaderAdditions) {
            for (const node of this.parents)
                if (node instanceof MaterialConnection && node.parameters.key === "window") {
                    onThreeRequestShaderAdditions(
                        "fragment",
                        `uniform mat4 modelViewMatrix;
                        uniform mat4 projectionMatrix;`,
                    )
                    break
                }
        }

        return {
            camera,
            generated: TSL.vec3(generated, 0),
            normal,
            object,
            reflection,
            uv: TSL.vec3(uv, 0),
            window: TSL.vec3(window, 0),
        }
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<TexCoordSockets>> => {
        const texCoordNode: TextureCoordinateNode = {
            type: "texture_coordinate",
            inputs: {},
            parameters: {
                from_dupli: undefined,
                ob_tfm: undefined,
                use_transform: undefined,
            },
            resources: {},
        }

        return {
            camera: [texCoordNode, "camera"],
            generated: [texCoordNode, "generated"],
            normal: [texCoordNode, "normal"],
            object: [texCoordNode, "object"],
            reflection: [texCoordNode, "reflection"],
            uv: [texCoordNode, "uv"],
            window: [texCoordNode, "window"],
        }
    }
}
